import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { Textarea } from '@/components/ui/Textarea'
import { useForm } from '@inertiajs/react'
import { FormEvent } from 'react'

interface Community {
  id: number
  name: string
}

interface ParentCategory {
  id: number
  name: string
  community_id: number
}

interface CategoryData {
  id?: number
  name: string
  description: string
  community_id: number | string
  parent_id: number | string | null
  position: number
}

interface CategoryFormProps {
  category?: CategoryData
  communities: Community[]
  parentCategories: ParentCategory[]
  maxPosition: number
  onSubmit: string
  isEdit?: boolean
}

export function CategoryForm({
  category,
  communities,
  parentCategories,
  maxPosition,
  onSubmit,
  isEdit = false
}: CategoryFormProps) {
  const { data, setData, post, put, processing, errors } = useForm({
    name: category?.name || '',
    description: category?.description || '',
    community_id: category?.community_id?.toString() || '',
    parent_id: category?.parent_id?.toString() || '',
    position: category?.position || maxPosition
  })

  // Filtrer les catégories parentes selon la communauté sélectionnée
  const filteredParentCategories = parentCategories.filter(
    (pc) => !data.community_id || pc.community_id.toString() === data.community_id
  )

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    if (isEdit && category?.id) {
      put(onSubmit)
    } else {
      post(onSubmit)
    }
  }

  return (
    <form onSubmit={handleSubmit}>
      <Card className="p-6">
        <div className="space-y-6">
          {/* Name */}
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-text-dark mb-1">
              Nom de la catégorie *
            </label>
            <Input
              id="name"
              type="text"
              value={data.name}
              onChange={(e) => setData('name', e.target.value)}
              placeholder="Ex: Cas cliniques, Actualités médicales..."
              required
            />
            {errors.name && (
              <p className="mt-1 text-sm text-red-600">{errors.name}</p>
            )}
          </div>

          {/* Description */}
          <div>
            <label htmlFor="description" className="block text-sm font-medium text-text-dark mb-1">
              Description
            </label>
            <Textarea
              id="description"
              value={data.description}
              onChange={(e) => setData('description', e.target.value)}
              placeholder="Décrivez brièvement le contenu de cette catégorie..."
              rows={3}
            />
            {errors.description && (
              <p className="mt-1 text-sm text-red-600">{errors.description}</p>
            )}
            <p className="mt-1 text-xs text-text-gray">
              Cette description aide les utilisateurs à comprendre le contenu de la catégorie.
            </p>
          </div>

          {/* Community */}
          <div>
            <label htmlFor="community_id" className="block text-sm font-medium text-text-dark mb-1">
              Communauté *
            </label>
            <Select
              id="community_id"
              value={data.community_id}
              onChange={(e) => {
                setData('community_id', e.target.value)
                // Reset parent si on change de communauté
                setData('parent_id', '')
              }}
              required
            >
              <option value="">Sélectionner une communauté</option>
              {communities.map((community) => (
                <option key={community.id} value={community.id}>
                  {community.name}
                </option>
              ))}
            </Select>
            {errors.community_id && (
              <p className="mt-1 text-sm text-red-600">{errors.community_id}</p>
            )}
          </div>

          {/* Parent Category */}
          <div>
            <label htmlFor="parent_id" className="block text-sm font-medium text-text-dark mb-1">
              Catégorie parente
            </label>
            <Select
              id="parent_id"
              value={data.parent_id}
              onChange={(e) => setData('parent_id', e.target.value)}
              disabled={!data.community_id}
            >
              <option value="">Aucune (catégorie racine)</option>
              {filteredParentCategories.map((parent) => (
                <option key={parent.id} value={parent.id}>
                  {parent.name}
                </option>
              ))}
            </Select>
            {errors.parent_id && (
              <p className="mt-1 text-sm text-red-600">{errors.parent_id}</p>
            )}
            <p className="mt-1 text-xs text-text-gray">
              Laissez vide pour créer une catégorie principale, ou sélectionnez un parent pour créer une sous-catégorie.
            </p>
          </div>

          {/* Position */}
          <div>
            <label htmlFor="position" className="block text-sm font-medium text-text-dark mb-1">
              Position
            </label>
            <Input
              id="position"
              type="number"
              min={0}
              max={100}
              value={data.position}
              onChange={(e) => setData('position', parseInt(e.target.value) || 0)}
            />
            {errors.position && (
              <p className="mt-1 text-sm text-red-600">{errors.position}</p>
            )}
            <p className="mt-1 text-xs text-text-gray">
              Les catégories sont triées par position (0 = premier). Valeur suggérée : {maxPosition}
            </p>
          </div>
        </div>

        {/* Submit */}
        <div className="mt-8 pt-6 border-t border-gray-200 flex justify-end gap-4">
          <Button type="button" variant="secondary" onClick={() => history.back()}>
            Annuler
          </Button>
          <Button type="submit" disabled={processing}>
            {processing ? 'Enregistrement...' : isEdit ? 'Mettre à jour' : 'Créer la catégorie'}
          </Button>
        </div>
      </Card>
    </form>
  )
}
